import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HelpIcon from '@mui/icons-material/Help';
import Button from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import { SelectableChip } from '@components/Chip';
import { ModalConfirm } from '@components/Modal';
import { Switch } from '@components/Switch';
import TagEditModal from '@components/TagEditModal';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { AiBlock, AiIcon, AiText } from '@components/AiButton';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import { surveysActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { reviewTagLabel } from '@helpers/reviewTags';
import { surveyService } from '@services';
import { GatedSwitch } from '@features/Paywall/GatedSwitch';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { MAX_AI_REVIEW_TAGS, MAX_REVIEW_TAG_NAME_LENGTH } from '@constants/surveys';
import usePermissions from '@hooks/usePermissions';
import { useTranslation, useOpenUpgradeDrawer } from '@hooks';

const Chips = styled('div')({ display: 'flex', flexWrap: 'wrap', gap: 8 });

const SwitchRow = styled('div')({ display: 'flex', alignItems: 'center', gap: 8 });

const SwitchLabel = ({ children }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <AiIcon />
        <AiText style={{ fontWeight: 500 }}>{children}</AiText>
    </span>
);

const AiReviewSuggestions = ({ isView, isQrOnlyPlan }) => {
    const { t } = useTranslation();
    const { control, setValue, getValues, watch } = useFormContext();
    const openUpgradeDrawer = useOpenUpgradeDrawer();
    const popupZIndex = usePopupZIndex();
    const dispatch = useDispatch();
    const { hasFullAccess } = usePermissions();
    const [addAnchorEl, setAddAnchorEl] = useState(null);
    const [newTagName, setNewTagName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [tagToEdit, setTagToEdit] = useState(null);
    const [tagToDelete, setTagToDelete] = useState(null);
    const [reviewTags, setReviewTags] = useState({ catalogue: [], industry: [], custom: [] });

    useEffect(() => {
        if (isQrOnlyPlan) return undefined;
        let cancelled = false;
        surveyService
            .getReviewTags()
            .then(result => !cancelled && result && setReviewTags(result))
            .catch(error => !cancelled && handleApiError(error));
        return () => {
            cancelled = true;
        };
    }, [isQrOnlyPlan]);

    const canManageCustomTags = !isView && hasFullAccess('surveys');
    const enabled = watch('aiReviewSuggestionsEnabled');
    const tags = watch('aiReviewTags') || [];
    const tagLabel = tag => reviewTagLabel(tag, t);
    const byLabel = (a, b) => tagLabel(a).localeCompare(tagLabel(b));
    const atCap = tags.length >= MAX_AI_REVIEW_TAGS;
    const standardTags = [
        ...[...reviewTags.industry].sort(byLabel),
        ...reviewTags.catalogue.filter(tag => !reviewTags.industry.includes(tag)).sort(byLabel),
    ];

    const setTags = (next, options = { shouldDirty: true }) => setValue('aiReviewTags', next, options);

    const prefillDefaultTags = () =>
        surveyService
            .getReviewTagDefaults()
            .then(defaults => {
                const stillWaiting = getValues('aiReviewSuggestionsEnabled') && !getValues('aiReviewTags')?.length;
                if (stillWaiting && defaults?.length) {
                    setTags(defaults);
                }
            })
            .catch(error => handleApiError(error));

    const removeTag = tag => setTags(tags.filter(item => item !== tag));

    const toggleTag = tag => {
        if (tags.includes(tag)) {
            removeTag(tag);
        } else if (!atCap) {
            setTags([...tags, tag]);
        }
    };

    const closeMenu = () => {
        setAddAnchorEl(null);
        setNewTagName('');
    };

    const createTag = async () => {
        const name = newTagName.trim();
        if (!name || isCreating) return;
        setIsCreating(true);
        try {
            const created = await surveyService.createReviewTag(name);
            setReviewTags(previous => ({ ...previous, custom: [...previous.custom, created] }));
            setNewTagName('');
            const current = getValues('aiReviewTags') || [];
            if (current.length < MAX_AI_REVIEW_TAGS) {
                setTags([...current, created.name]);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsCreating(false);
        }
    };

    const renameTag = async ({ id, name }) => {
        const previous = reviewTags.custom.find(tag => tag.id === id)?.name;
        try {
            const renamed = await surveyService.renameReviewTag(id, name);
            setReviewTags(current => ({
                ...current,
                custom: current.custom.map(tag => (tag.id === id ? renamed : tag)),
            }));
            const current = getValues('aiReviewTags') || [];
            setTags(
                current.map(tag => (tag === previous ? renamed.name : tag)),
                { shouldDirty: false },
            );
            setTagToEdit(null);
            dispatch(surveysActions.getAll());
        } catch (error) {
            handleApiError(error);
        }
    };

    const deleteTag = async tag => {
        setTagToDelete(null);
        try {
            await surveyService.deleteReviewTag(tag.id);
            setReviewTags(current => ({ ...current, custom: current.custom.filter(item => item.id !== tag.id) }));
            setTags(
                (getValues('aiReviewTags') || []).filter(item => item !== tag.name),
                { shouldDirty: false },
            );
            dispatch(surveysActions.getAll());
        } catch (error) {
            handleApiError(error);
        }
    };

    const renderRow = (tag, customTag) => {
        const checked = tags.includes(tag);
        const blocked = !checked && atCap;
        return (
            <MenuItem
                key={customTag?.id ?? tag}
                onClick={() => !blocked && toggleTag(tag)}
                aria-disabled={blocked}
                sx={{ gap: '8px', '&.MuiMenuItem-root': { minHeight: 38 } }}
            >
                <Checkbox
                    checked={checked}
                    disabled={blocked}
                    sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '1.125rem' } }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: blocked ? 'var(--text-tertiary)' : undefined,
                    }}
                >
                    {tagLabel(tag)}
                </Typography>
                {customTag && canManageCustomTags && (
                    <>
                        <IconButton
                            size="small"
                            aria-label={t('FeedbackSurveys.editReviewTagTitle')}
                            onClick={event => {
                                event.stopPropagation();
                                closeMenu();
                                setTagToEdit(customTag);
                            }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            aria-label={t('FeedbackSurveys.deleteReviewTagTitle')}
                            onClick={event => {
                                event.stopPropagation();
                                closeMenu();
                                setTagToDelete(customTag);
                            }}
                        >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </>
                )}
            </MenuItem>
        );
    };

    if (isQrOnlyPlan) {
        return (
            <GatedSwitch
                gated
                disabled={isView}
                label={t('FeedbackSurveys.aiReviewSuggestions')}
                onUpgradeClick={() =>
                    openUpgradeDrawer({
                        defaultPlan: BILLING_TYPE_MAP.PROFESSIONAL,
                        titleKey: 'Paywall.upgradeForCustomerRating',
                        subtitleKey: 'Paywall.upgradeForCustomerRatingDescription',
                    })
                }
            />
        );
    }

    return (
        <>
            <SwitchRow>
                <Controller
                    control={control}
                    name="aiReviewSuggestionsEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('aiReviewSuggestionsEnabled')}
                            handleChange={event => {
                                const turnedOn = event.target.checked;
                                setValue('aiReviewSuggestionsEnabled', turnedOn, { shouldDirty: true });
                                if (turnedOn && !tags.length) {
                                    prefillDefaultTags();
                                }
                            }}
                            label={<SwitchLabel>{t('FeedbackSurveys.aiReviewSuggestions')}</SwitchLabel>}
                            disabled={isView || (!enabled && !reviewTags.catalogue.length)}
                        />
                    )}
                />
                <IconButton sx={{ padding: '4px !important' }}>
                    <Tooltip
                        title={t('FeedbackSurveys.aiReviewSuggestionsTooltip')}
                        placement="top"
                        enterTouchDelay={0}
                    >
                        <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                    </Tooltip>
                </IconButton>
            </SwitchRow>
            {enabled && (
                <AiBlock>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', marginBottom: '10px' }}>
                        {t('FeedbackSurveys.aiReviewTags')}
                    </Typography>
                    <Chips>
                        {tags.map(tag => (
                            <SelectableChip
                                key={tag}
                                label={tagLabel(tag)}
                                disabled={isView}
                                onDelete={isView ? undefined : () => removeTag(tag)}
                            />
                        ))}
                        {!isView && (
                            <Button
                                type="button"
                                variant="text"
                                sx={{ height: 30, minWidth: 0, padding: '4px 8px' }}
                                onClick={event => setAddAnchorEl(event.currentTarget)}
                            >
                                <AddRoundedIcon sx={{ fontSize: 16 }} />
                                {t('FeedbackSurveys.addReviewTag')}
                            </Button>
                        )}
                    </Chips>
                    <Menu
                        anchorEl={addAnchorEl}
                        open={!!addAnchorEl}
                        onClose={closeMenu}
                        autoFocus={!canManageCustomTags}
                        sx={{
                            zIndex: popupZIndex,
                            '.MuiMenu-paper': { borderRadius: '8px', maxHeight: 320, minWidth: 240 },
                        }}
                    >
                        {canManageCustomTags && (
                            <div style={{ padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                                <InputBase
                                    autoFocus
                                    placeholder={t('FeedbackSurveys.reviewTagNamePlaceholder')}
                                    value={newTagName}
                                    onChange={event => setNewTagName(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key !== 'Escape' && event.key !== 'Tab') {
                                            event.stopPropagation();
                                        }
                                        if (event.key === 'Enter') {
                                            createTag();
                                        }
                                    }}
                                    inputProps={{ maxLength: MAX_REVIEW_TAG_NAME_LENGTH }}
                                    sx={{ fontSize: 14, flex: 1 }}
                                />
                                <Button
                                    type="button"
                                    variant="text"
                                    sx={{ height: 24, minWidth: 0, padding: '4px 8px', whiteSpace: 'nowrap' }}
                                    disabled={!newTagName.trim() || isCreating}
                                    onClick={createTag}
                                >
                                    {t('FeedbackSurveys.createReviewTag')}
                                </Button>
                            </div>
                        )}
                        {reviewTags.custom.map(customTag => renderRow(customTag.name, customTag))}
                        {standardTags.map(tag => renderRow(tag))}
                    </Menu>
                </AiBlock>
            )}
            {!!tagToEdit && (
                <TagEditModal
                    tag={tagToEdit}
                    title={t('FeedbackSurveys.editReviewTagTitle')}
                    maxLength={MAX_REVIEW_TAG_NAME_LENGTH}
                    onClose={() => setTagToEdit(null)}
                    onSave={renameTag}
                />
            )}
            {!!tagToDelete && (
                <ModalConfirm
                    isOpen
                    handleClose={() => setTagToDelete(null)}
                    title={t('FeedbackSurveys.deleteReviewTagTitle')}
                    description={t('FeedbackSurveys.deleteReviewTagDescription', { name: tagToDelete.name })}
                    onPrimaryAction={() => deleteTag(tagToDelete)}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </>
    );
};

export default AiReviewSuggestions;
